"use client"

import { useEffect, useState } from "react"
import type { FabCorner } from "@/hooks/use-fab-corner"
import { FAB_CORNER_TRANSFORM } from "@/hooks/use-fab-auto-corner"
import type { TextDirection } from "@/lib/i18n/direction"

/**
 * Keeping the Feedback FAB off the app sidebar (staging #970 item 9, #509).
 *
 * The FAB's corners are measured from the edge of the WINDOW (FAB_CORNER_TRANSFORM).
 * On the creation routes it steps to the bottom corner at the inline start to
 * leave the wizard's primary button alone - and that corner is where the sidebar
 * is, with the account switcher in its footer. So a corner on the sidebar's side
 * is measured from the edge of the page content instead: the sidebar's width is
 * added to the margin.
 *
 * The width is the room the sidebar holds in the page layout (`sidebar-gap`):
 * 16rem open, the icon rail collapsed, 0 when it is off-canvas or, on a narrow
 * window, a sheet (the gap is not rendered then). It is read from the DOM
 * because the FAB host lives above the shell that owns the sidebar's state.
 */

const SIDEBAR_GAP_SELECTOR = '[data-slot="sidebar-gap"]'

/** The sidebar is at the inline start of the page: the left in English, the right in Arabic. */
function sidebarSide(direction: TextDirection): "left" | "right" {
  return direction === "rtl" ? "right" : "left"
}

/**
 * `FAB_CORNER_TRANSFORM[corner]`, moved in by `insetPx` when the corner is on
 * the sidebar's side of the page. With no inset, or on the other side, it is
 * the plain corner.
 */
export function fabCornerTransformFor(
  corner: FabCorner,
  insetPx: number,
  direction: TextDirection = "ltr",
): string {
  const side = corner.endsWith("right") ? "right" : "left"
  if (insetPx <= 0 || side !== sidebarSide(direction)) return FAB_CORNER_TRANSFORM[corner]
  const y = corner.startsWith("bottom") ? "calc(100vh - 100% - 1.5rem)" : "1.5rem"
  const x = side === "left" ? `calc(1.5rem + ${insetPx}px)` : `calc(100vw - 100% - 1.5rem - ${insetPx}px)`
  return `translate(${x}, ${y})`
}

/**
 * The width, in px, of the room the app sidebar holds in the layout right now
 * (0 when there is none). Re-read when the route changes (the layout that holds
 * the sidebar may have just mounted or gone) and whenever the sidebar resizes
 * (collapsed to its rail, opened again). Until the sidebar exists it watches
 * for it to be added; once found it stops watching the page.
 */
export function useSidebarInset(pathname: string | null): number {
  const [inset, setInset] = useState(0)

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    let gap: Element | null = null
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => read())
    let mutationObserver: MutationObserver | null = null

    function read() {
      const next = document.querySelector(SIDEBAR_GAP_SELECTOR)
      if (next !== gap) {
        if (gap) resizeObserver?.unobserve(gap)
        gap = next
        if (gap) resizeObserver?.observe(gap)
      }
      setInset(gap ? Math.round(gap.getBoundingClientRect().width) : 0)
      // Not there yet: the layout that holds it may mount a beat after this host.
      if (!gap && !mutationObserver && typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver(read)
        mutationObserver.observe(document.body, { childList: true, subtree: true })
      } else if (gap && mutationObserver) {
        mutationObserver.disconnect()
        mutationObserver = null
      }
    }

    read()
    return () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
    }
  }, [pathname])
  /* eslint-enable react-hooks/set-state-in-effect */

  return inset
}
